/**
 * The server admin's report queue (F6): list, read the quoted line, resolve
 * as removed or dismissed. Reachable only when `account.isAdmin` — the
 * server answers 403 to anyone else, this screen just never draws for them.
 */
import { For, type JSX, Show, createResource, createSignal } from "solid-js";

import { currentLifetime, isCurrent } from "../lib/lifetime";
import * as api from "../lib/api/rest";
import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { Runtime } from "../store/runtime";
import { ActivityIndicator, Icon } from "../components/ds";

export function ReportsScreen(props: { rt: Runtime; t: Dict; onClose(): void }): JSX.Element {
  const rt = props.rt;
  const t = props.t;
  const [all, setAll] = createSignal(false);
  // The source is wrapped in an object: a bare boolean source means "do not
  // fetch while falsy" to createResource, and `all = false` is our default.
  const [rows, { refetch }] = createResource(
    () => ({ all: all() }),
    (q) => api.fetchAdminReports(q.all),
  );

  async function resolve(id: number, resolution: "removed" | "dismissed"): Promise<void> {
    const lifetime = currentLifetime();
    try {
      await api.resolveReport(id, resolution);
      if (!isCurrent(lifetime)) return;
      void refetch();
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      rt.showNotice(e instanceof Error ? e.message : String(e));
    }
  }

  const nameOf = (id: number | null) =>
    id === null ? "—" : mut.nameOf(rt.state.directory, id);

  return (
    <div class="echo-scrim echo-transition-enter" onClick={() => props.onClose()}>
      <div style="display:contents" onClick={(e) => e.stopPropagation()}>
        <div class="profile-screen surface-pane echo-material-thick admin-console admin-console--reports">
          <div class="profile-screen__header">
            <button
              type="button"
              class="echo-button echo-button--medium echo-button--borderless"
              onClick={() => props.onClose()}
            >
              <Icon name="close" size={15} />
              {t.consoleCancel()}
            </button>
            <span class="profile-screen__title">{t.reportsTitle()}</span>
            <label class="reports-screen__toggle">
              <input
                type="checkbox"
                class="echo-checkbox"
                checked={all()}
                onChange={(e) => setAll(e.currentTarget.checked)}
              />
              {t.reportsShowResolved()}
            </label>
          </div>
          <div class="profile-screen__body admin-console__body">
            <Show when={!rows.loading} fallback={<ActivityIndicator />}>
              <div class="echo-list-section">
                <div class="echo-list-section__title">{t.reportsOpen(rows()?.open ?? 0)}</div>
                <Show
                  when={(rows()?.reports.length ?? 0) > 0}
                  fallback={<div class="echo-empty-state">{t.reportsEmpty()}</div>}
                >
                  <div class="echo-list-section__card">
                    <For each={rows()?.reports ?? []}>
                      {(r) => (
                        <div class="echo-list-row echo-list-row--auto-height reports-screen__row">
                          <div class="reports-screen__column">
                            <span class="echo-list-row__title">
                              {t.reportReasonLabel(r.reason)} · {nameOf(r.author_id)}
                            </span>
                            <span class="reports-screen__quote">{r.quoted_text}</span>
                            <Show when={r.comment !== ""}>
                              <span class="echo-list-row__subtitle">{r.comment}</span>
                            </Show>
                            <span class="echo-list-row__subtitle">
                              {new Date(r.created_at_ms).toLocaleString()} ·{" "}
                              {nameOf(r.reporter_id)}
                              <Show when={r.resolution}>
                                {" · "}
                                {r.resolution}
                              </Show>
                            </span>
                          </div>
                          <span class="layout-spacer" />
                          <Show when={r.resolved_at_ms === null}>
                            <button
                              type="button"
                              class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
                              onClick={() => void resolve(r.id, "removed")}
                            >
                              <Icon name="delete" size={14} /> {t.reportsRemove()}
                            </button>
                            <button
                              type="button"
                              class="echo-button echo-button--medium echo-button--secondary"
                              onClick={() => void resolve(r.id, "dismissed")}
                          >
                            <Icon name="close" size={15} />
                            {t.reportsDismiss()}
                            </button>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
